import { default as React, useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from 'react-query';
import { MiniLoader } from '@croquiscom-pvt/zds';
import styled from '@emotion/styled';
import { Provider } from 'jotai';
import compact from 'lodash/compact';
import isEqual from 'lodash/isEqual';

import { UBLProvider } from '@common/log-manager';
import { useUBL } from '@common/log-manager';
import { Drawer, Portal } from '@common/overlay-manager';
import { Gray_Light_White } from '@common/styles';
import { ThemeProvider } from '@common/styles/theme-provider';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { PdpConfigProvider } from '@domains/pdp-common';

import { Container } from '../containers/Container';
import { CommonView } from '../containers/options-view/CommonView';
import { useOptionsControl } from '../hooks/useOptionsControl';
import { useOptions } from '../queries/useOptions';
import { CustomTextOption, ItemCardType } from '../types/option-type';

enum StatusType {
  'SOLD_OUT' = 'SOLD_OUT',
  'NORMAL' = 'NORMAL',
}

export interface CartItem {
  additional_option_detail_list: {
    name: string;
    value: string;
  }[];
  name: string;
  status: keyof typeof StatusType;
  option_type: string;
  catalog_product_id: string;
  product_item_id: string;
  quantity: number;
  bogo_promotion_ids?: string[];
}

interface Props {
  id?: string;
  isAdditional?: boolean;
  productId?: string;
  maximumLimit?: number;
  cartItem: CartItem | null;
  open: boolean;
  onEdit?: (items: ItemCardType[]) => void;
  onClose?: () => void;
}

/**
 * 입력형 옵션 목록과 장바구니 상품의 입력된 옵션 목록이 일치하는지 확인하는 함수
 */
function isEqualsTextOptions(
  textOptions: CustomTextOption[],
  additional_option_detail_list: CartItem['additional_option_detail_list'],
) {
  // 입력형 옵션이 존재하지만, 장바구니 상품에서 입력된 옵션이 없다면 선택옵션 선택하지 않은 경우이기 때문에 pass
  if (textOptions.length > 0 && additional_option_detail_list.length === 0) {
    const isNotSelected = textOptions.every((option) => option.selected_value === '');

    return isNotSelected;
  }

  // 입력형 옵션 목록과 장바구니 상품의 입력된 옵션 목로가 일치하는지 확인한다.
  return textOptions.every((option) => {
    const currentOption = additional_option_detail_list.find((item) => item.name === option.name); // 필수 옵션은 무조건 존재
    if (currentOption) {
      return option.selected_value === currentOption.value;
    }

    return option.selected_value === ''; // 존재하지 않는 경우는 선택옵션 이 경우 빈 문자열이라면 pass
  });
}

const CartOptionChangeView = ({
  isAdditional,
  maximumLimit,
  cartItem,
  open,
  onEdit,
  onClose = () => {
    // empty
  },
}: Props) => {
  const sendUBL = useUBL();
  const queryClient = useQueryClient();
  const { isFetched } = useOptions();
  const [isLoaded, setIsLoaded] = useState(false);
  const {
    removeAllItems,
    additional_options,
    itemList,
    removeItem,
    isNoOption,
    itemCards,
    addItem,
    makeSelectedItem,
    options,
    textOptions,
    setMaximumCount,
    is_bogo,
  } = useOptionsControl();
  const visibleOptionSelect = isAdditional ? !isAdditional : !isNoOption;
  const isEditDisabled = checkIsEditDisabled(cartItem, itemCards);

  const isComplete = isLoaded && isFetched;

  /**
   * 기존 상품과 현재 상품이 동일한지 체크한다.
   * @param cartItem 기존 장바구니 상품
   * @param selectedItems 선택된 현재 상품
   * @returns
   */
  function checkIsEditDisabled(cart_item?: CartItem | null, selected_items?: ItemCardType[]) {
    try {
      if (!cart_item) {
        throw new Error('cart_item is not defined');
      }

      const { product_item_id, quantity, additional_option_detail_list, bogo_promotion_ids } = cart_item;

      if (selected_items) {
        const [selected_item] = selected_items;
        const ids = selected_item.item.origin_items.map((item) => item.id);

        if (selected_item) {
          const {
            count,
            item: { id, text_options },
          } = selected_item;
          const [text_option] = text_options;
          const is_match_quantity_and_text_option =
            quantity === count && isEqualsTextOptions(text_option, additional_option_detail_list);

          // BOGO 프로모션 케이스가 있으면 bogo_promotion_ids 비교
          if (bogo_promotion_ids) {
            return isEqual(bogo_promotion_ids, ids) && is_match_quantity_and_text_option;
          }

          return product_item_id === id && is_match_quantity_and_text_option;
        }
      }

      return false;
    } catch {
      return false;
    }
  }

  function pageviewUBL(open: boolean) {
    if (cartItem) {
      const { catalog_product_id } = cartItem;
      const pageUrl = window.location.href;

      sendUBL({
        category: 'pageview',
        navigation: 'zpay_browser_cart_option',
        navigation_sub: { page_url: pageUrl },
        data: { catalog_product_id, is_opened: open },
      });
    }
  }

  function handleEditClick() {
    onEdit?.(itemCards);
  }

  useEffect(() => {
    if (cartItem) {
      pageviewUBL(true);

      return () => pageviewUBL(false);
    }
  }, [cartItem]);
  /**
   * 장바구니에서 매번 새로운 catalog_id를 기반으로 옵션 리스트를 가져와야 하기 때문에
   * Drawer가 닫힐 때, queryClient, 선택된 모든 아이템들을 초기화한다.
   */
  useEffect(() => {
    if (!open) {
      return () => {
        queryClient.clear();
        removeAllItems();
        setIsLoaded(false);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * 편집모드일 경우, 전달받은 cartItem을 기준으로 초기화한다.
   */
  useEffect(() => {
    function initCartItem() {
      try {
        if (!cartItem || isLoaded) return;

        const { additional_option_detail_list, name, status, quantity, product_item_id } = cartItem;

        // 장바구니 상품이 품절일 경우 동작시키지 않는다.
        if (status === StatusType.SOLD_OUT) return;

        // 추가상품일 경우, 추가상품 리스트에서 선택된 상품을 찾아서 추가한다.
        if (isAdditional && additional_options) {
          const { item_list } = additional_options[0];
          const item = item_list.find((item) => item.id === product_item_id);

          if (item) {
            const refinedItem = makeSelectedItem(
              [
                {
                  ...item,
                  discount_info: null,
                  final_price: 0,
                  expected_delivery_time: null,
                  remain_stock: null,
                },
              ],
              quantity,
              {
                options: [],
                text_options: [],
                is_bogo: false,
              },
            );

            return addItem(refinedItem);
          }
        }

        // 추가상품이 아닐 경우, 상품 리스트에서 선택된 상품을 찾아서 추가한다.
        if (!isAdditional) {
          const matchedItem = itemList.find((item) => item.name === name);
          if (matchedItem) {
            const finalSelectedItems = is_bogo
              ? compact(cartItem.bogo_promotion_ids?.map((id) => itemList.find((item) => item.id === id)))
              : [matchedItem];

            const refinedOptions = options.map((optionList, index) => {
              return optionList.map((option, i) => {
                const { value_id: selected_value_id, value: selected_value } = is_bogo
                  ? finalSelectedItems[index].item_attribute_list[i]
                  : matchedItem.item_attribute_list[i];

                return {
                  ...option,
                  selected_value,
                  selected_value_id,
                };
              });
            });

            const refinedTextOptions = textOptions.map((textOption) =>
              textOption.map((option) => {
                const selected_value =
                  additional_option_detail_list.find((item) => item.name === option.name)?.value ?? '';

                return {
                  ...option,
                  selected_value,
                };
              }),
            );

            const refinedItem = makeSelectedItem(finalSelectedItems, quantity, {
              options: refinedOptions,
              text_options: refinedTextOptions,
              is_bogo,
            });

            return addItem(refinedItem);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoaded(true);
      }
    }

    if (itemList.length > 0) {
      initCartItem();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemList]);

  /**
   * 최대 선택 가능한 수량을 설정한다.
   */
  useEffect(() => {
    if (maximumLimit) {
      setMaximumCount(maximumLimit);
    }
  }, [maximumLimit]);

  /**
   * 1개 이상 아이템이 선택되면, 상품변경 정책으로 인해 기존 아이템을 제거한다.
   */
  useEffect(() => {
    if (itemCards.length > 1) {
      const { item } = itemCards[0];
      removeItem(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCards]);

  /**
   * TODO: 로딩인디케이터 추가
   */

  if (!options) {
    return null;
  }

  return (
    <Portal>
      <ThemeProvider colorScheme='light'>
        <Drawer open={open} onClose={onClose} maxHeight={'calc(100vh - 16px)'} height='65vh'>
          <Container>
            <Wrapper>
              {isComplete ? (
                <CommonView
                  isLoggedIn={false}
                  onClose={onClose}
                  onEdit={handleEditClick}
                  isEditable
                  visibleAdditional={isAdditional}
                  visibleOptionSelect={visibleOptionSelect}
                  isEditDisabled={isEditDisabled}
                  disabledAutoSelect
                  isCouponUnused
                />
              ) : (
                <Loader />
              )}
            </Wrapper>
          </Container>
        </Drawer>
      </ThemeProvider>
    </Portal>
  );
};

export const CartOptionChangeScreen = (props: Props) => {
  const queryClientRef = useRef<QueryClient>(new QueryClient());

  return (
    <Provider>
      <PdpConfigProvider
        isZigzagApp
        isProductPreview
        catalogProductId={props.id}
        path={{
          auth: {
            login: '/login',
          },
          review: {
            list: () => '/review',
          },
        }}
      >
        <UBLProvider navigation={'zpay_browser_cart_option'}>
          <QueryClientProvider client={queryClientRef.current}>
            <CartOptionChangeView {...props} />
          </QueryClientProvider>
        </UBLProvider>
      </PdpConfigProvider>
    </Provider>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${Gray_Light_White};
`;

const Loader = styled(MiniLoader)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
